'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';

export default function TermsPage() {
  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      body: (
        <>
          <p className="mb-4">
            By accessing and using Shashio&apos;s services, you accept and agree to be bound by the terms and
            provisions of this agreement. If you do not agree to these terms, please do not use our services.
          </p>
          <p>
            These Terms of Service apply to all users of the service, including without limitation users who are
            browsers, vendors, customers, merchants, and/or contributors of content.
          </p>
        </>
      ),
    },
    {
      id: 'user-accounts',
      title: 'User Accounts',
      body: (
        <>
          <p className="mb-4">
            To access certain features of our service, you must register for an account. When you register, you
            agree to provide accurate, current, and complete information about yourself.
          </p>
          <p className="mb-4">
            You are responsible for maintaining the confidentiality of your account credentials and for all
            activities that occur under your account. You agree to notify us immediately of any unauthorized use of
            your account.
          </p>
          <p>
            We reserve the right to suspend or terminate your account if any information provided during
            registration or thereafter proves to be inaccurate, false, or misleading.
          </p>
        </>
      ),
    },
    {
      id: 'service-usage',
      title: 'Service Usage',
      body: (
        <>
          <p className="mb-4">Our services are provided for lawful purposes only. You agree not to use our services:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>In any way that violates any applicable federal, state, local, or international law</li>
            <li>To transmit any material that is defamatory, offensive, or otherwise objectionable</li>
            <li>To impersonate or attempt to impersonate Shashio, a Shashio employee, another user, or any other person or entity</li>
            <li>To engage in any conduct that restricts or inhibits anyone&apos;s use or enjoyment of the service</li>
          </ul>
        </>
      ),
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      body: (
        <>
          <p className="mb-4">
            The service and its original content, features, and functionality are and will remain the exclusive
            property of Shashio and its licensors. The service is protected by copyright, trademark, and other laws
            of both the United States and foreign countries.
          </p>
          <p className="mb-4">
            You retain all rights to the images you upload to our service. By uploading images, you grant Shashio a
            limited license to process, edit, and deliver the images back to you as part of our service.
          </p>
          <p>
            We will not use your images for any purpose other than providing our editing services, unless you
            explicitly grant us permission to use them for marketing or promotional purposes.
          </p>
        </>
      ),
    },
    {
      id: 'liability',
      title: 'Limitations of Liability',
      body: (
        <>
          <p className="mb-4">
            In no event shall Shashio, nor its directors, employees, partners, agents, suppliers, or affiliates, be
            liable for any indirect, incidental, special, consequential, or punitive damages, including without
            limitation, loss of profits, data, use, goodwill, or other intangible losses.
          </p>
          <p className="mb-4">
            We do not guarantee that the service will be uninterrupted, timely, secure, or error-free. We do not
            warrant that the results obtained from the use of the service will be accurate or reliable.
          </p>
          <p>
            Our total liability to you for any claims arising from or related to these terms or our services shall
            not exceed the amount you paid us in the twelve (12) months preceding the claim.
          </p>
        </>
      ),
    },
    {
      id: 'dispute-resolution',
      title: 'Dispute Resolution',
      body: (
        <>
          <p className="mb-4">
            Any disputes arising out of or relating to these Terms of Service or our services shall be resolved
            through binding arbitration in accordance with the rules of the American Arbitration Association.
          </p>
          <p className="mb-4">
            The arbitration shall take place in the state where Shashio&apos;s principal office is located. The
            arbitrator&apos;s decision shall be final and binding, and judgment may be entered upon it in any court of
            competent jurisdiction.
          </p>
          <p>
            Notwithstanding the foregoing, either party may seek injunctive or other equitable relief in any court
            of competent jurisdiction to prevent the actual or threatened infringement, misappropriation, or
            violation of a party&apos;s intellectual property rights.
          </p>
        </>
      ),
    },
    {
      id: 'changes',
      title: 'Changes to Terms',
      body: (
        <>
          <p className="mb-4">
            We reserve the right to modify or replace these Terms of Service at any time at our sole discretion. If
            a revision is material, we will provide at least 30 days&apos; notice prior to any new terms taking effect.
          </p>
          <p>
            By continuing to access or use our service after those revisions become effective, you agree to be bound
            by the revised terms. If you do not agree to the new terms, please stop using the service.
          </p>
        </>
      ),
    },
    {
      id: 'contact',
      title: 'Contact Information',
      body: (
        <>
          <p className="mb-4">If you have any questions about these Terms of Service, please contact us:</p>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-6 space-y-2">
            <p><strong className="text-white">Email:</strong> <a href="mailto:legal@shashio.com" className="text-purple-400 hover:text-purple-300 transition-colors">legal@shashio.com</a></p>
            <p><strong className="text-white">Website:</strong> www.shashio.com</p>
            <p><strong className="text-white">Address:</strong> Shashio Legal Department</p>
          </div>
        </>
      ),
    },
  ];

  const dotGrid = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  return (
    <div className="min-h-screen bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17]">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-0 right-1/3 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: dotGrid }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium mb-8">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>The Fine Print</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-5">
            Terms of{' '}
            <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
              Service.
            </span>
          </h1>
          <p className="text-lg text-gray-400">Last updated: January 1, 2026</p>
        </div>
      </section>

      {/* ===================== CONTENT ===================== */}
      <section className="relative py-16 lg:py-24 px-6 lg:px-12 border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[260px_1fr] gap-10 xl:gap-16">
          {/* Table of contents */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">On this page</p>
              <nav className="space-y-1">
                {sections.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/[0.04] transition-all duration-200"
                  >
                    <span className="text-purple-400 font-semibold mr-2">{i + 1}.</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Sections */}
          <div className="space-y-6">
            <div className="bg-[#13151F]/60 border border-white/[0.08] rounded-2xl p-6 lg:p-8 text-gray-400 leading-relaxed">
              These Terms of Service govern your use of Shashio&apos;s real estate photo editing platform and services.
              Please read them carefully — by using Shashio, you agree to everything below.
            </div>

            {sections.map((section, i) => (
              <div
                key={section.id}
                id={section.id}
                className="scroll-mt-24 bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-6 lg:p-8 hover:border-purple-500/20 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg shadow-purple-500/30">
                    {i + 1}
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="text-gray-400 leading-relaxed">{section.body}</div>
              </div>
            ))}

            {/* Footer CTA */}
            <div className="relative overflow-hidden rounded-2xl border-2 border-white/[0.12] bg-[#13151F]/95 backdrop-blur-2xl p-8 lg:p-10 text-center">
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-purple-600/15 rounded-full blur-[120px]" />
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-3">Questions about these terms?</h3>
                <p className="text-gray-400 mb-6 max-w-xl mx-auto">
                  Our team is happy to clarify anything about how our service works.
                </p>
                <Link href="/contact">
                  <button className="px-8 py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold rounded-xl transition-all duration-300 shadow-lg shadow-purple-500/30 hover:-translate-y-0.5">
                    Contact Us
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
